import { FC } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { getField } from "@simplyk/common";

import { CampaignCard } from "../../CampaignCard/CampaignCard";
import { getEndedCampaignHoverLabel, getEventCampaignCategory } from "../../CampaignCard/helpers/campaignCategory";
import { EventRowsDay } from "../helpers/groupEventRowsByDay";
import { useSecondaryTextColor } from "../helpers/useSecondaryTextColor";
import { EventsTab } from "../helpers/useEventRowsTab";

import { EventRowDetails } from "./EventRowDetails/EventRowDetails";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { OCCURRENCE_ID_QUERY_PARAM } from "@/common/helpers/ticketingOccurrence";
import { appendQueryParams } from "@/common/helpers/url";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormTypography } from "@/forms-design-system/FormTypography";

const TIMELINE_DOT_SIZE = 1;

interface EventRowsTimelineProps {
  days: EventRowsDay[];
  tab: EventsTab;
}

export const EventRowsTimeline: FC<EventRowsTimelineProps> = ({ days, tab }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const secondaryTextColor = useSecondaryTextColor();

  return (
    <Stack
      gap={2}
      data-test="org-page-event-rows"
      sx={(theme) => ({
        position: "relative",
        paddingLeft: { xs: 0, sm: 3 },
        "&::before": {
          content: '""',
          display: { xs: "none", sm: "block" },
          position: "absolute",
          left: theme.spacing(TIMELINE_DOT_SIZE / 2),
          top: theme.spacing(1.5),
          bottom: 0,
          borderLeft: `1px dashed ${theme.palette.forms.border.supershy}`,
        },
      })}
    >
      {days.map((day) => (
        <Stack key={day.dayKey} gap={2}>
          <Box
            sx={(theme) => ({
              position: "relative",
              "&::before": {
                content: '""',
                display: { xs: "none", sm: "block" },
                position: "absolute",
                left: theme.spacing(-3),
                top: theme.spacing(1),
                width: theme.spacing(TIMELINE_DOT_SIZE),
                height: theme.spacing(TIMELINE_DOT_SIZE),
                borderRadius: "50%",
                backgroundColor: theme.palette.forms.text.supershy,
              },
            })}
          >
            <FormTypography variant="subtitle1" vibe="moderate" component="p">
              {`${day.dayLabel} `}
              <FormTypography variant="body1" color={secondaryTextColor} component="span">
                {`| ${day.weekdayLabel}`}
              </FormTypography>
            </FormTypography>
          </Box>
          {day.rows.map(({ row, event, formattedDate }) => {
            const category = getEventCampaignCategory(event.formCategory);
            return (
              <CampaignCard
                key={row.occurrenceId}
                variant="event"
                title={getField(event.ticketingFields, locale)?.title || ""}
                url={
                  tab === "upcoming" && event.isMultiDate
                    ? appendQueryParams(event.url, { [OCCURRENCE_ID_QUERY_PARAM]: row.occurrenceId })
                    : event.url
                }
                category={category}
                bannerUrl={event.bannerUrl || null}
                bannerVideoUrl={event.bannerVideoUrl ?? null}
                isSoldOut={row.isSoldOut}
                isSalesClosed={row.isSalesClosed}
                hoverLabel={tab === "past" ? getEndedCampaignHoverLabel(category, t) : undefined}
                onClick={() =>
                  tab === "past"
                    ? logAmplitudeEvent(AmplitudeEvents.OrgPagePastEventsViewed)
                    : logAmplitudeEvent(AmplitudeEvents.OrgPageCampaignOpened, { campaign_type: category })
                }
                slots={{
                  details: (
                    <EventRowDetails
                      formattedDate={formattedDate}
                      isMultiDate={event.isMultiDate}
                      address={event.address || null}
                    />
                  ),
                }}
              />
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
};
